import { useLocation, useSearchParams } from 'react-router-dom'
import { compact, isEmpty, isEqual } from 'lodash'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  FilterObject,
  FilterOperator,
  FilterableColumn,
  FilterValueType,
  decodeFilters,
  encodeFilters,
  getReadableOperator,
} from 'app/filter.utils'
import { buildClearedFilterParams } from 'app/lib/clearFilterParams'
import { useHandleSearchParams } from 'app/utils'

export type FilterType = {
  attribute?: string
  secondaryAttribute?: string
  displayName?: string
  operator?: FilterOperator
  displayOperator?: string
  value?: string | string[]
  secondaryValue?: string[]
  customSearchParam?: string | string[]
  nonEmpty?: boolean
  group?: string
}

export const emptyFilter: FilterType = { attribute: ``, operator: undefined, value: `` }

export type TableFiltersProps = {
  className?: string
  columns?: FilterableColumn[]
  customSearchParam?: string
  defaultJoin?: `and` | `or`
  useOrOption?: boolean
  enableDeepLinking?: boolean
  initialFilters?: FilterObject[]
  onFiltersChange?: (encoded: string) => void
}

const areFiltersEqual = (defaultFilters?: FilterType[], selectedFilters?: FilterType[]): boolean | undefined => {
  if (defaultFilters?.length !== selectedFilters?.length) return false
  return defaultFilters?.every((defaultFilter) => {
    const filter = selectedFilters?.find((selectedFilter) => selectedFilter.attribute === defaultFilter.attribute)
    if (!filter) return false
    const defaultValue = Array.isArray(defaultFilter.value) ? defaultFilter.value.slice().sort() : defaultFilter.value
    const filterValue = Array.isArray(filter.value) ? filter.value.slice().sort() : filter.value
    return (
      filter.attribute === defaultFilter.attribute &&
      filter.operator === defaultFilter.operator &&
      isEqual(defaultValue, filterValue)
    )
  })
}

function getStandaloneFilterAttributes(columns?: FilterableColumn[]): string[] {
  if (!columns) return []
  return compact(columns.filter((column) => column.standalone).map((column) => column.attribute))
}

function shouldExcludeFilter(attribute: string | undefined, standaloneFilterAttributes: string[]): boolean {
  if (!attribute || !standaloneFilterAttributes.includes(attribute)) return false
  return true
}

function getAppliedFilters(
  filters: FilterType[],
  standaloneFilterAttributes: string[],
  empty: FilterType,
): FilterType[] {
  if (isEqual(filters, [empty])) return [empty]
  const filtered = filters.filter(
    (filter) => filter.attribute && !standaloneFilterAttributes.includes(filter.attribute),
  )
  return filtered.length === 0 || (filtered.length === 1 && isEqual(filtered[0], empty)) ? [empty] : filtered
}

export function useTableFilters({
  columns,
  customSearchParam = `filters`,
  defaultJoin,
  useOrOption = true,
  enableDeepLinking = true,
  initialFilters,
  onFiltersChange,
}: TableFiltersProps) {
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const handleSearchParams = useHandleSearchParams()
  const [canCreateList, setCanCreateList] = useState(false)
  const [filters, setFilters] = useState<FilterType[]>([emptyFilter])
  const [appliedTerms, setAppliedTerms] = useState<FilterType[] | undefined>()
  const [joinChanged, setJoinChanged] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [join, setJoin] = useState<`and` | `or`>(() => {
    if (!useOrOption) return `and`
    if (searchParams.get(`join`) === `and` || searchParams.get(`join`) === `or`) {
      return searchParams.get(`join`) as `and` | `or`
    }
    return defaultJoin ?? `and`
  })

  const pageSupportNewList = [`/contacts`, `/activities`].includes(location.pathname)
  const standaloneFilterAttributes = getStandaloneFilterAttributes(columns)

  const defaultFilters = useMemo(() => columns?.filter((column) => column.defaultFilter) ?? [], [columns])

  const filtersFromURL = useMemo(() => {
    if (!enableDeepLinking) return []
    let urlFilters: FilterObject[] | FilterType[] = decodeFilters(searchParams.get(customSearchParam))
    let defaultFilter: FilterableColumn[] | undefined = columns?.filter((column) => column.customSearchParam)
    let loaderFilterValues = compact([...(defaultFilter ?? [])])?.reduce((acc, filter) => {
      if (filter.customSearchParam) {
        let value = compact((searchParams.get(filter.customSearchParam) ?? ``)?.split(`,`))
        // A value the column does not offer (an old `band=all`) is no rule, not a row naming nothing.
        if (filter.options)
          value = value.filter(
            (v) =>
              filter.options!.some((option) => option.value === v) ||
              !!filter.dayWindows?.some((choice) => v.startsWith(`${choice.kind}:`)),
          )
        if (value?.length) return { ...acc, [filter.attribute]: value }
      }
      return acc
    }, {})
    let loaderFilterOperators = compact([...(defaultFilter ?? [])])?.reduce(
      (acc, filter) => {
        if (filter.customSearchParam && typeof filter.customSearchParam === `string`) {
          let operator = searchParams.get(`${filter.customSearchParam}Op`) as FilterOperator | null
          // Ignore a literal "undefined" (a stale value from before the write-side
          // fix) so the term falls back to the column's default operator.
          if (operator && (operator as string) !== `undefined`) return { ...acc, [filter.attribute]: operator }
        }
        return acc
      },
      {} as Record<string, FilterOperator>,
    )
    urlFilters = compact([...urlFilters])?.map<FilterType>((filter) => {
      let selectedColumn = columns?.find((column) => column.attribute === filter.attribute)
      return {
        attribute: filter.attribute,
        displayName: selectedColumn?.displayName,
        operator: filter?.operator,
        displayOperator: getReadableOperator(filter.operator!, selectedColumn?.type!),
        value: Array.isArray(filter.value) ? compact(filter.value) : filter.value,
        nonEmpty: filter.nonEmpty,
      }
    })
    let loaderFilterFromUrl = compact([...(defaultFilter ?? [])])?.map<FilterType | null>((filter) => {
      let selectedColumn = columns?.find((column) => column.attribute === filter.attribute)
      if (!isEmpty(loaderFilterValues)) {
        let operator = loaderFilterOperators[filter.attribute] ?? filter?.defaultOperator
        return loaderFilterValues[filter.attribute]
          ? {
              attribute: filter.attribute,
              displayName: selectedColumn?.displayName,
              operator,
              displayOperator: getReadableOperator(operator!, selectedColumn?.type!),
              value:
                loaderFilterValues[filter.attribute].length > 1 || selectedColumn?.type === FilterValueType.multiple
                  ? loaderFilterValues[filter.attribute]
                  : loaderFilterValues[filter.attribute][0],
              ...(filter.customSearchParam && { customSearchParam: filter.customSearchParam }),
            }
          : null
      }
      return null
    })
    return [...compact(loaderFilterFromUrl), ...urlFilters]
  }, [searchParams, enableDeepLinking])

  const defaultFilterTypes = useMemo(() => {
    let preAppliedTerms: FilterableColumn[] = columns?.filter((column) => column.defaultFilter) ?? []
    if (preAppliedTerms.length === 0) return []
    return preAppliedTerms.map<FilterType>((t) => ({
      attribute: t.attribute,
      displayName: t?.displayName,
      operator: t?.defaultOperator,
      displayOperator: getReadableOperator(t.defaultOperator!, t?.type!),
      value: Array.isArray(t.defaultValue)
        ? t.defaultValue.slice().sort((a, b) => (a > b ? 1 : b > a ? -1 : 0))
        : t.defaultValue,
      ...(t.customSearchParam && { customSearchParam: t.customSearchParam }),
    }))
  }, [columns])

  const paramDefaultTypes = useMemo(
    () =>
      (columns ?? [])
        .filter(
          (column) =>
            column.paramDefault &&
            typeof column.customSearchParam === `string` &&
            column.defaultValue !== undefined &&
            !searchParams.has(column.customSearchParam),
        )
        .map<FilterType>((column) => ({
          attribute: column.attribute,
          displayName: column.displayName,
          operator: column.defaultOperator,
          displayOperator: getReadableOperator(column.defaultOperator!, column.type!),
          value: column.defaultValue,
          customSearchParam: column.customSearchParam,
        })),
    [columns, searchParams],
  )

  const initialFilterTypes = useMemo(() => {
    if (!initialFilters || initialFilters.length === 0) return []
    return initialFilters.map<FilterType>((filter) => {
      const selectedColumn = columns?.find((column) => column.attribute === filter.attribute)
      return {
        attribute: filter.attribute,
        displayName: filter.displayName || selectedColumn?.displayName,
        operator: filter.operator,
        displayOperator: getReadableOperator(filter.operator, selectedColumn?.type!),
        value: Array.isArray(filter.value) ? compact(filter.value) : filter.value,
      }
    })
  }, [initialFilters, columns])

  useEffect(() => {
    let urlFilters: FilterType[] = filtersFromURL ?? []
    let filtersCustomSearchParams = defaultFilterTypes.flatMap((filter) => filter.customSearchParam)
    const effectiveDefaultFilters = initialFilterTypes.length > 0 ? initialFilterTypes : defaultFilterTypes
    if (filtersFromURL.length) {
      setCanCreateList(!areFiltersEqual(effectiveDefaultFilters, urlFilters))
    } else {
      setCanCreateList(false)
    }
    const resolved = [
      ...paramDefaultTypes,
      ...(urlFilters.length
        ? urlFilters
        : searchParams.has(customSearchParam) ||
            filtersCustomSearchParams?.some((param) => param && searchParams.has(param))
          ? []
          : effectiveDefaultFilters),
    ]
    setAppliedTerms(resolved)
    setFilters(resolved.length ? resolved : [emptyFilter])
  }, [filtersFromURL, defaultFilterTypes, initialFilterTypes, paramDefaultTypes])

  const applyFilters = useCallback(
    (nextFilters?: FilterType[]) => {
      const source = nextFilters ?? filters
      const applyTerms = (source ?? []).filter((term) => {
        const column = columns?.find((c) => c.attribute === term.attribute)
        return (
          column &&
          ((term.attribute && term.operator && term.value) ||
            (column.defaultFilter && (term.value ?? column.defaultValue)))
        )
      })
      if (areFiltersEqual(appliedTerms, applyTerms) && !joinChanged) return
      if (applyTerms?.length === 0) {
        setFilters([emptyFilter])
        setAppliedTerms([])
        setCanCreateList(false)
        onFiltersChange?.(``)
        if (enableDeepLinking) {
          handleSearchParams(buildClearedFilterParams({ columns, searchParams, customSearchParam }))
        }
        return
      }
      setFilters(applyTerms)
      const validFilters = applyTerms?.filter(
        (term) => !columns?.find((c) => c.attribute === term.attribute)?.customSearchParam,
      )
      const loaderFilters = applyTerms?.filter(
        (term) => columns?.find((c) => c.attribute === term.attribute)?.customSearchParam,
      )
      const secondaryAttributeFilters = applyTerms?.filter((f) =>
        columns?.find((c) => f.secondaryValue && c.secondaryAttribute === f.secondaryAttribute),
      )
      const currentUrlFilters: FilterObject[] = decodeFilters(searchParams.get(customSearchParam))
      const existingStandaloneFilters = currentUrlFilters.filter((f) =>
        standaloneFilterAttributes.includes(f.attribute),
      )
      const allFilters = [...existingStandaloneFilters, ...(validFilters as FilterObject[])]
      const encodedFilters = encodeFilters(allFilters)
      const encodedSecondaryFilters = secondaryAttributeFilters.reduce(
        (acc, f) => ({ ...acc, [f.secondaryAttribute!]: f.secondaryValue?.join(`,`) }),
        {},
      )
      setAppliedTerms(applyTerms)
      onFiltersChange?.(encodedFilters)
      const removeCustomSearchParams = appliedTerms
        ?.filter((term) => term.customSearchParam)
        ?.reduce(
          (acc, term) => {
            const params = Array.isArray(term.customSearchParam) ? term.customSearchParam : [term.customSearchParam]
            const paramDefault = columns?.find((c) => c.attribute === term.attribute)?.paramDefault
            params.forEach((param) => {
              if (param) acc[param] = paramDefault ? `` : undefined
            })
            return acc
          },
          {} as Record<string, any>,
        )
      const params = {
        ...removeCustomSearchParams,
        ...(searchParams.has(customSearchParam) && { [customSearchParam]: undefined }),
        ...(applyTerms && applyTerms.length > 1 && useOrOption && { join: join as `and` | `or` }),
        ...(encodedFilters && { [customSearchParam]: encodedFilters }),
        ...(!isEmpty(encodedSecondaryFilters) && encodedSecondaryFilters),
        ...(loaderFilters &&
          loaderFilters.reduce((acc, filter) => {
            const customParam = filter.customSearchParam
            if (Array.isArray(customParam)) {
              return customParam.reduce((acc2, param, index) => {
                const value = filter?.value?.[index]
                return value !== undefined ? { ...acc2, [param]: value } : acc2
              }, acc)
            } else if (customParam) {
              const column = columns?.find((col) => col.customSearchParam === customParam)
              const operatorParam =
                filter.operator && filter.operator !== column?.defaultOperator
                  ? { [`${customParam}Op`]: filter.operator }
                  : { [`${customParam}Op`]: undefined }
              return {
                ...acc,
                [customParam]: Array.isArray(filter.value) ? filter.value.join(`,`) : filter.value,
                ...operatorParam,
              }
            }
            return acc
          }, {})),
      }
      enableDeepLinking && handleSearchParams(params as Record<string, string>)
    },
    [
      filters,
      appliedTerms,
      joinChanged,
      columns,
      join,
      searchParams,
      customSearchParam,
      standaloneFilterAttributes,
      enableDeepLinking,
      handleSearchParams,
      onFiltersChange,
      useOrOption,
    ],
  )

  const removeFilter = useCallback(
    (nextFilters: FilterType[]) => {
      setFilters(nextFilters.length ? nextFilters : [emptyFilter])
      applyFilters(nextFilters)
    },
    [applyFilters],
  )

  const clearFilters = useCallback(() => {
    setFilters([emptyFilter])
    setAppliedTerms([])
    onFiltersChange?.(``)
    setCanCreateList(false)
    if (enableDeepLinking) {
      // Shared with each table shell's own Clear button so the three cannot
      // drift apart again. It derives the secondary-attribute params from the
      // declared columns rather than the currently-active filters, which is a
      // superset: clearing one that was never set is a no-op.
      handleSearchParams(buildClearedFilterParams({ columns, searchParams, customSearchParam }))
    }
  }, [columns, customSearchParam, searchParams, enableDeepLinking, onFiltersChange, handleSearchParams])

  const handleFilterChange = useCallback(
    (newFilters: FilterType[]) => {
      setCanCreateList(
        newFilters.every((f) => f.attribute && f.operator && f.value) &&
          !areFiltersEqual(defaultFilterTypes, newFilters),
      )
      setFilters(newFilters)
    },
    [defaultFilterTypes],
  )

  const handleJoinChange = useCallback(
    (value: `and` | `or`) => {
      setJoin(value)
      setJoinChanged(true)
      onFiltersChange?.(encodeFilters(appliedTerms as FilterObject[]))
    },
    [appliedTerms],
  )

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (isOpen && !open) applyFilters()
      setIsOpen(open)
    },
    [isOpen, applyFilters],
  )

  const filteredAppliedTermsCount =
    appliedTerms?.filter((term) => !shouldExcludeFilter(term.attribute, standaloneFilterAttributes)).length || 0

  const dropdownFilters = getAppliedFilters(filters, standaloneFilterAttributes, emptyFilter)

  return {
    isOpen,
    setOpen: setIsOpen,
    filters,
    appliedTerms,
    join,
    canCreateList,
    pageSupportNewList,
    filteredAppliedTermsCount,
    dropdownFilters,
    standaloneFilterAttributes,
    defaultFilters,
    handleOpenChange,
    handleFilterChange,
    handleJoinChange,
    applyFilters,
    removeFilter,
    clearFilters,
  }
}
